import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import AcademicAssistantUI from "./AcademicAssistant";
import StatsPage from "./StatsPage";
import AdminDashboard from "./AdminDashboard";
import ChallengePage from "./ChallengePage";
import UserProfilePanel from "./components/UserProfilePanel";
import FloatingChatWidget from "./components/FloatingChatWidget";
import HiddenGenius from "./components/HiddenGenius";

import "./fonts.css";

// Separate component inside BrowserRouter so useNavigate works correctly
function AppContent() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Global Session and Navigation UI States
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showHistoryMobile, setShowHistoryMobile] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackType, setFeedbackType] = useState("bug");
  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [showSocialMenu, setShowSocialMenu] = useState(false);

  // Global Chat State
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Battle Promo Popup State
  const [showBattlePopup, setShowBattlePopup] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthLoading(false);
      if (!user) {
        setShowBattlePopup(true);
      } else {
        setShowBattlePopup(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleGoogleLogin = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      setShowBattlePopup(false);
      navigate("/profile"); // Navigate to profile upon successful login
    } catch (error) {
      console.error("Google Login Error:", error);
    }
  };

  if (authLoading) {
    return (
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        minHeight: "100vh", backgroundColor: "#0f172a", fontFamily: "Inter, sans-serif", gap: "16px"
      }}>
        <div style={{
          width: "36px", height: "36px", border: "4px solid #1e293b", borderTop: "4px solid #f97316",
          borderRadius: "50%", animation: "spin 0.8s linear infinite"
        }}></div>
        <p style={{ color: "#94a3b8", fontWeight: "500" }}>Loading application...</p>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<AcademicAssistantUI currentUser={currentUser} onOpenChat={() => setIsChatOpen(true)} />} />
        <Route path="/challenge" element={<ChallengePage currentUser={currentUser} onOpenChat={() => setIsChatOpen(true)} />} />
        
        {/* User Profile Panel Route */}
        <Route 
          path="/profile" 
          element={
            <UserProfilePanel 
              currentUser={currentUser} 
              sessions={sessions}
              currentSessionId={currentSessionId}
              showHistoryMobile={showHistoryMobile}
              setShowHistoryMobile={setShowHistoryMobile}
              showFeedback={showFeedback}
              setShowFeedback={setShowFeedback}
              feedbackText={feedbackText}
              setFeedbackText={setFeedbackText}
              feedbackType={feedbackType}
              setFeedbackType={setFeedbackType}
              showFeedbackSuccess={showFeedbackSuccess}
              confirmingDelete={confirmingDelete}
              setConfirmingDelete={setConfirmingDelete}
              showSocialMenu={showSocialMenu}
              setShowSocialMenu={setShowSocialMenu}
              onOpenChat={() => setIsChatOpen(true)}
            />
          } 
        />

        {/* Hidden Genius Route with complete navigation & drawer props */}
        <Route
          path="/genius-test"
          element={
            <HiddenGenius
              currentUser={currentUser}
              sessions={sessions}
              currentSessionId={currentSessionId}
              showHistoryMobile={showHistoryMobile}
              setShowHistoryMobile={setShowHistoryMobile}
              showFeedback={showFeedback}
              setShowFeedback={setShowFeedback}
              feedbackText={feedbackText}
              setFeedbackText={setFeedbackText}
              feedbackType={feedbackType}
              setFeedbackType={setFeedbackType}
              showFeedbackSuccess={showFeedbackSuccess}
              confirmingDelete={confirmingDelete}
              setConfirmingDelete={setConfirmingDelete}
              showSocialMenu={showSocialMenu}
              setShowSocialMenu={setShowSocialMenu}
              onOpenChat={() => setIsChatOpen(true)}
            />
          }
        />

        <Route path="/stats/today" element={<StatsPage type="today" />} />
        <Route path="/stats/cost" element={<StatsPage type="cost" />} />
        <Route path="/_internal/usage-dashboard" element={<AdminDashboard />} />
      </Routes>

      {/* Global Floating Chat Widget */}
      <FloatingChatWidget 
        isOpen={isChatOpen} 
        onClose={() => setIsChatOpen(false)} 
      />
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}